<script lang="ts">
/**
 * The visual statuses this component can render — its public option set.
 * The component never interprets backend values; adapters (statusMaps.ts)
 * translate API enums into one of these options at the call site.
 */
export type GaiaIndicatorStatus =
  | 'alert'
  | 'running'
  | 'done'
  | 'risk'
  | 'success'
  | 'info'
  | 'warning'
  | 'neutral'
  | 'pending'
  | 'accent';
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaText from './GaiaText.vue';
import { gaiaStatus } from '../themes/tokens';

const props = defineProps({
  status: {
    type: String as PropType<GaiaIndicatorStatus>,
    default: 'neutral',
  },
  /** dot | check; default auto (check for "done", dot otherwise). The File
      Detail extraction rows use check with "success"/"failed" colors. */
  glyph: {
    type: String as PropType<'dot' | 'check' | undefined>,
    default: undefined,
  },
});

// naive-ui has no status-dot primitive (n-badge's dot can't express the
// running pulse or the done check), so this component is self-contained.
// All colors come from the shared status/neutral tokens.
const showCheck = computed(() => (props.glyph ? props.glyph === 'check' : props.status === 'done'));
const statusColor = computed(() => gaiaStatus[props.status]);
const pulseFrom = gaiaStatus.runningPulseFrom;
const pulseTo = gaiaStatus.running;
</script>

<template>
  <n-flex inline align="center" :size="8" :wrap="false" class="gaia-status-indicator">
    <!-- Every glyph is drawn inside the same square (see `.glyph`), so which
         one a row happens to show can't move the row's text. -->
    <n-flex
      align="center"
      justify="center"
      :size="0"
      :wrap="false"
      :style="{ flex: 'none', width: '13px', height: '13px' }"
    >
      <svg
        v-if="showCheck"
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        :stroke="statusColor"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M4 12.5 9.5 18 20 6.5" />
      </svg>
      <span
        v-else
        class="dot"
        :class="{ running: props.status === 'running' }"
        :style="{ background: props.status === 'running' ? undefined : statusColor }"
      />
    </n-flex>
    <gaia-text v-if="$slots.default" tone="text"><slot /></gaia-text>
  </n-flex>
</template>

<style scoped>
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
}

.dot.running {
  width: 8px;
  height: 8px;
  animation: gaia-actpulse 1.4s ease-in-out infinite;
}

@keyframes gaia-actpulse {
  0%,
  100% {
    background: v-bind(pulseFrom);
    transform: scale(0.85);
  }
  50% {
    background: v-bind(pulseTo);
    transform: scale(1);
  }
}
</style>
